import API_BASE from '../config';
import { Link } from 'react-router-dom';
import React from 'react';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../common/utils';
import Spinner from './Spinner';

const resolveImg = (val) => {
  if (!val) return '';
  if (/^https?:\/\//i.test(val)) return val;
  const clean = val.replace(/^uploads\//, '/');
  return API_BASE + (clean.startsWith('/') ? clean : '/' + clean);
};

const money = (n) => formatCurrency(Number(n || 0));

const Cart = () => {
  const { t } = useTranslation();
  const { cart, loading, updateQuantity, removeItem, clearCart } = useCart();

  const changeQty = (item, delta) => {
    const next = item.quantity + delta;
    if (next < 1) return;
    updateQuantity(item.id, next);
  };

  if (loading && (!cart.items || cart.items.length === 0)) {
    return (
      <Spinner />
    );
  }

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className='container mt-4'>
        <h3>{t('cart.title')}</h3>
        <p className='text-muted'>{t('cart.empty')}</p>
        <Link to='/catalog' className='btn btn-primary'>
          {t('cart.browseProducts')}
        </Link>
      </div>
    );
  }

  return (
    <div className='container mt-4'>
      <h3 className='mb-3'>{t('cart.title')}</h3>

      <div className='table-responsive rounded'>
        <table className='table table-hover align-middle'>
          <thead>
            <tr>
              <th style={{ width: 100 }}>{t('cart.image')}</th>
              <th>{t('cart.product')}</th>
              <th style={{ width: 120 }}>{t('cart.price')}</th>
              <th style={{ width: 160 }}>{t('cart.qty')}</th>
              <th style={{ width: 130 }}>{t('cart.subtotal')}</th>
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {cart.items.map((it) => (
              <tr key={it.id}>
                <td className='text-center'>
                  {it.image_url ? (
                    <img
                      src={resolveImg(it.image_url)}
                      alt={it.name}
                      style={{ maxHeight: 48, maxWidth: 64, objectFit: 'contain' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span className='text-muted'>—</span>
                  )}
                </td>
                <td>{it.name}</td>
                <td>{money(it.unit_price)}</td>
                <td>
                  <div className='input-group input-group-sm' style={{ maxWidth: 140 }}>
                    <button className='btn btn-outline-secondary' onClick={() => changeQty(it, -1)} disabled={it.quantity <= 1}>
                      −
                    </button>
                    <input
                      type='number'
                      min='1'
                      className='form-control text-center'
                      value={it.quantity}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (Number.isInteger(v) && v >= 1) updateQuantity(it.id, v);
                      }}
                    />
                    <button className='btn btn-outline-secondary' onClick={() => changeQty(it, 1)}>
                      +
                    </button>
                  </div>
                </td>
                <td className='fw-bold'>{money(it.line_total)}</td>
                <td>
                  <button className='btn btn-sm btn-outline-danger' onClick={() => removeItem(it.id)}>
                    {t('cart.remove')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='d-flex justify-content-between align-items-center'>
        <button className='btn btn-outline-secondary' onClick={clearCart}>
          {t('cart.clearCart')}
        </button>
        <div className='text-end'>
          <h4 className='mb-3'>
            {t('cart.total')}: <span className='text-primary'>{money(cart.total_amount)}</span>
          </h4>
          <Link to='/checkout' className='btn btn-primary btn-md'>
            {t('cart.proceedToCheckout')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Cart;
